import { FC, ReactNode } from 'react';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { alpha, Box, ButtonBase } from '@mui/material';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { QcsTypography } from '../common/basic/QcsTypography';
import { QcsChip } from '../common/basic/QcsChip';
import { KeyFormValues } from './integrationUtils';
import { ApiAuthMethod } from '@api/models/enums/ApiAuthMethod';

//The recommended (more secure) method goes first.
const AUTH_METHODS = [ApiAuthMethod.SigningKey, ApiAuthMethod.ApiKey];

const Point: FC<{ icon: ReactNode; children: ReactNode }> = ({ icon, children }) => (
  <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
    {icon}
    <QcsTypography variant="body2">{children}</QcsTypography>
  </Box>
);

/** Only when creating - the method can't be changed later. Each tile says what the method means, its advantage and its drawback. */
export const IntegrationAuthMethodField: FC = () => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<KeyFormValues>();

  return (
    <>
      <Box
        role="radiogroup"
        aria-label={t('integration.apiAccess.authMethod.title')}
        sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}
      >
        {AUTH_METHODS.map((authMethod) => {
          const selected = values.authMethod === authMethod;

          return (
            <ButtonBase
              key={authMethod}
              role="radio"
              aria-checked={selected}
              onClick={() => setFieldValue('authMethod', authMethod)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: 1.5,
                p: 2,
                textAlign: 'left',
                borderRadius: 2,
                border: '2px solid',
                borderColor: selected ? 'primary.main' : 'divider',
                bgcolor: (theme) => (selected ? alpha(theme.palette.primary.main, 0.06) : 'background.paper'),
                transition: 'border-color 0.2s ease, background-color 0.2s ease',
                '&:hover': { borderColor: selected ? 'primary.main' : 'text.secondary' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {selected ? <RadioButtonCheckedIcon color="primary" /> : <RadioButtonUncheckedIcon color="action" />}
                <QcsTypography variant="h6" sx={{ fontSize: '1.1rem' }}>
                  {t(`integration.apiAccess.authMethod.${authMethod}`)}
                </QcsTypography>
                {authMethod === ApiAuthMethod.SigningKey && (
                  <QcsChip label={t('integration.apiAccess.authMethod.recommended')} color="success" sx={{ ml: 'auto' }} />
                )}
              </Box>
              <QcsTypography variant="body2" sx={{ color: 'text.secondary' }}>
                {t(`integration.apiAccess.authMethod.summary.${authMethod}`)}
              </QcsTypography>
              <Point icon={<CheckCircleOutlineIcon fontSize="small" color="success" />}>
                {t(`integration.apiAccess.authMethod.pros.${authMethod}`)}
              </Point>
              <Point icon={<RemoveCircleOutlineIcon fontSize="small" color="warning" />}>
                {t(`integration.apiAccess.authMethod.cons.${authMethod}`)}
              </Point>
            </ButtonBase>
          );
        })}
      </Box>
      <QcsTypography variant="body2" sx={{ color: 'text.secondary' }}>
        {t('integration.apiAccess.authMethod.fixed')}
      </QcsTypography>
    </>
  );
};
